import React from 'react';

import { Link } from 'react-router-dom';
import colorsVars from 'styles/colors.module.scss';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';
import { compareNumericString, onlyNumbers } from 'utilitys/helpers/general';

import { ILogicCheckerCategoryResultsResponse } from 'api/queries/getLogicCheckerCategoryResults.query';
import { ILogicCheckerConfigurationResponse } from 'api/queries/getLogicCheckerConfiguration.query';
import { IValidationTypeDrillDownResponse } from 'api/queries/getValidationTypeDrillDown.query';

import { TooltipCellWithCopy } from 'components/common/Tables/Cells/TooltipCellWithCopy/TooltipCellWithCopy';

import { riskConfig } from 'components/Dashboards/ExcellenceApps/riskConfig';

const { statusSummaryGreen, statusSummaryRed } = colorsVars;

export const passColor = statusSummaryGreen;
export const failColor = statusSummaryRed;

/**
 *
 * @param testValue
 * @param configObject
 * @return {string|string|string|string}
 */
export const getColor = (testValue, configObject) => {
    // The coloredDot wants to see a red/yellow/green string, to then choose between three gradient backgrounds
    // for text, we want the exact color that the API returns as color
    // thus we can use returnApiColor to select between these two
    // check if we actually have a config, and tresholds in the config, if not return grey
    if (!configObject || configObject?.thresholds?.length < 1) {
        return 'grey';
    }

    let upperLimitOperator = configObject.thresholds[0].upper_limit_operator;
    let upperLimit = configObject.thresholds[0].upper_limit_value;

    const low = configObject.thresholds[0].color_low_level;
    const high = configObject.thresholds[0].color_high_level;

    let lower = low === 'PASS' ? passColor : failColor;
    let upper = high === 'PASS' ? passColor : failColor;
    if (upperLimitOperator === '>=') {
        return testValue >= upperLimit ? upper : lower;
    }
    if (upperLimitOperator === '>') {
        return testValue > upperLimit ? upper : lower;
    }
    if (upperLimitOperator === '<') {
        return testValue < upperLimit ? lower : upper;
    }
    if (upperLimitOperator === '<=') {
        return testValue <= upperLimit ? lower : upper;
    }

    return 'grey';
};

export const getValidationColumns = ({
    validationTypeDrillDown,
    validation,
    check,
    versionHashCode = undefined,
}: {
    validationTypeDrillDown: IValidationTypeDrillDownResponse | undefined;
    validation: ILogicCheckerConfigurationResponse | undefined;
    check: string;
    versionHashCode?: number | undefined;
}) => {
    if (validationTypeDrillDown && validation && check) {
        //  get column names
        const apiHeaders = validationTypeDrillDown.headers;

        // // make columns-array, return it
        return apiHeaders.map((column) => {
            // not all properties in a validation should map to a column
            const notValidColumns = ['activityDrilldown'];
            if (notValidColumns.includes(column.accessor)) {
                return false;
            } else {
                let generatedColumn: GeneratedColumn = {
                    header: column.header,
                    accessorKey: column.accessor,
                    Filter: false,
                    colorable: column.colorable,
                    clickable: column.clickable,
                    cell: ({ getValue }) => getValue(),
                };

                // Add tooltips if needed
                const tooltipCells = ['wbs name', 'wbsName'];
                if (tooltipCells.includes(column.header.toLowerCase().trim())) {
                    generatedColumn.cell = ({ getValue }) => <TooltipCellWithCopy text={getValue()} />;
                }

                // Add widths to column, we want to add up to 1452px for level 1/2
                // and here we just add reasonable values, and calculate total width to pass to react-window later on

                let preppedAccessor = generatedColumn.accessorKey.trim().toLowerCase();
                if (preppedAccessor === 'range') {
                    generatedColumn.sortType = compareNumericString;
                }

                // Add color to a column based on the value in it, based on the validation config and the value of the cell
                if (column.colorable) {
                    generatedColumn.cell = ({ getValue }) => {
                        // Calculate the color for this cell.
                        // For that we need the validationConfig
                        let calculatedColor = '';
                        if (check && validation) {
                            // getColor wants an int, but sometimes we are supplied a string with %, parseInt ignores % and makes an int out of it
                            let intValue = Number.parseFloat(onlyNumbers(getValue())); //parseInt(value);

                            calculatedColor = getColor(intValue, validation);
                        }

                        return <p style={{ color: calculatedColor, fontWeight: 'bold' }}>{getValue()}</p>;
                    };
                }

                // Add click functionality to cell based on clickable property
                if (column.clickable) {
                    generatedColumn.cell = ({ getValue, row }: { getValue: any; row?: any }) => {
                        const safeCheck = check.replace('/', '_');

                        const fullPath = AppRoutes.buildPathWithQuery(
                            [
                                AppRoutes.DASHBOARD_PLANNING_PATH,
                                riskConfig.logicQualityCheck.link,
                                versionHashCode,
                                'All',
                                safeCheck,
                                'activities',
                            ],
                            { accessor: generatedColumn.accessorKey, rowId: row.id },
                        );
                        const relativePath = `${safeCheck}/activities?accessor=${generatedColumn.accessorKey}&rowId=${row.id}`;
                        // If versionHashCode exists create full path link, used in report for full reroute
                        const path = versionHashCode ? fullPath : relativePath;
                        const value = getValue();
                        return value === 0 ? (
                            value
                        ) : (
                            <Link className={'turquoise'} to={path}>
                                {value}
                            </Link>
                        );
                    };
                }
                return generatedColumn;
            }
        });
    }

    return [];
};

interface GeneratedColumn {
    header: any;
    accessorKey: any;
    Filter: boolean;
    colorable: boolean;
    clickable: boolean;
    cell: ({ getValue }: { getValue: any }) => any;
    sortType?: any;
}

// check if current obj is 'whole project' row based on wholeProjectKeys list
/**
 * check if current row in table is type of Whole Project and needed to be uplifted
 * @param obj - row table
 * @return {this is string[]}
 */
export const isWholeProject = (obj) => {
    return Object.keys(obj).every((i) => {
        const value = obj[i];
        if (typeof value === 'string') {
            return value.toLowerCase() !== 'whole project';
        } else {
            return true;
        }
    });
};

export const getCheck = ({
    categoriesSummaryRows,
    parsedCategory,
    parsedCheck,
}: {
    categoriesSummaryRows: ILogicCheckerCategoryResultsResponse['categoriesSummaryRows'] | undefined;
    parsedCategory: string;
    parsedCheck: string;
}) => {
    if (categoriesSummaryRows && categoriesSummaryRows?.length > 0) {
        const pickedCategory = categoriesSummaryRows.find((oneCategory) => oneCategory.category === parsedCategory);
        return pickedCategory
            ? pickedCategory.validationRows.find((oneCheck) => oneCheck.validationName === parsedCheck)
            : undefined;
    }
    return undefined;
};
